/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useState, useEffect } from 'react';
import { Toolbar } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { dashboardActions } from 'modules/dashboard/slice';
import { searchActions } from 'modules/dashboard/components/tab-container/search/slice';
import { selectOrganization } from 'modules/common/auth/selectors';
import {
  selectBreakdown,
  selectDateComparison,
  selectSearchAlignmentToggle,
  selectSymmetricComparisonToggle,
  selectSearchOtb,
  selectSearchCustomDate,
  selectIsDetailed,
  selectSearchEndDate,
  selectSearchStartDate,
  selectSearchVisualToggle,
  selectAlignment,
  selectSymmetricComparisonIsDisplay,
  selectEnableSecondaryHotelGroups,
  selectHotelFocusOn,
  selectNormalizedView,
  selectSearchHotelComparison,
  selectOriginalHotelFocusOn,
  selectIsSearchLeapComparison,
  selectPercentage,
  selectIsMovingAverage,
  selectIsCumulative,
  selectMovingAverage,
  selectTrendTimeWeeklyView,
  selectFocusOn,
  selectSelectedScenario,
  selectSearchData,
} from 'modules/dashboard/components/tab-container/search/selectors';
import {
  selectPaceHorizon,
  selectPaceVariable,
  selectTargetDate,
  selectVisualFilters,
  selectPmsSync,
  selectIsSignedIn,
  selectSummaryForecastFilter,
  selectHotelName,
  selectHotelGroupDropdownList,
  selectHotelDropdownList,
  selectMergedHotelList,
  selectsHotelForecast,
  selectIsDemoModeActivated,
  selectIsSecondaryHotelSetOverTimeDisabled,
  selectIsSecondaryHotelSetOverTime,
  selectInvalidSecondaryHotelList,
  selectResetBookingDate,
  selectBreakdownDetails,
} from 'modules/dashboard/selectors';
import { getComparisonDateRange, getSelectedBreakdown } from 'modules/dashboard/functions';
import { CustomStack } from 'modules/common/components';
import { capitalizeFirst } from 'modules/common/helpers/strings';
import { TAGS, OTB_VALUE, KEYS, STAY_DATE, ALIGNED, labelOrder } from 'modules/dashboard/constants';
import ActionButton from 'modules/common/components/action-button';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import DataUpdatedLabel from 'modules/dashboard/components/tab-container/data-updated-label';
import PmsToggle from 'modules/dashboard/components/tab-container/inner-filter/components/pms-toggle';
import BookingWindow from 'modules/dashboard/components/tab-container/inner-filter/components/booking-window';
import BreakdownWindow from 'modules/dashboard/components/tab-container/inner-filter/components/breakdown-window';
import VsComparisonWindow from 'modules/dashboard/components/tab-container/inner-filter/components/vs-comparison-window';
import BookingPaceWindow from 'modules/dashboard/components/tab-container/inner-filter/components/booking-pace-window';
import {
  getDateComparisonFilter,
  getFocusOnFilter,
  getMovingAverageFilter,
  getPercentageList,
  getSubAlignmentFilter,
  getSubBreakdownFilter,
  getSubPaceFilter,
  getSubPaceVariableFilter,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';
import { isEmpty } from '@grafana/faro-web-sdk';
import { CompareHotelsWindow, PercentageWindow } from '../../../inner-filter';
import { ENVIRONMENT } from '../../../../../../../config';
import useSearchFilter from '../../hooks/use-search-filters';
import {
  COMMON_FOCUS_ON_OPTIONS,
  COMPARISON_TO_FILTER_MAP,
  FILTERS,
  SCENARIO_FILTERS,
} from '../../constants';
import MovingAverageWindow from '../../../inner-filter/components/moving-average-window';
import { updateScenarioFilter } from '../../functions';
/**
 * Filters for Search widget include PMS Sync, Booking Dates, Chart Breakdown,
 * Alignment, Date Comparison, Pace Horizon, pace variable and pace forecast
 * @param {Number} tabValue - Pace widget tab value
 * @param {Date} latestDate - Latest booking date of selected hotel
 * @param {Boolean} hotelTimezone - Timezone of selected hotel
 * @param {Number} toggleDisabled - PMS sync toggle disabled state
 * @param {Boolean} isLoaded - Visual is loaded state
 * @param {String} reportType - Report type of active tab
 * @param {Object} visual- Symmetric comparison visual object
 * @param {String} visualName - Display name for symmetric comparison visual
 * @returns
 */
const SearchFilter = ({
  availableFilters,
  reportType,
  toggleDisabled,
  latestDate,
  hotelTimezone,
  isLoaded,
}) => {
  const dispatch = useDispatch();
  const organization = useSelector(selectOrganization);
  // Selected filter values are obtained from redux store. Refer each selector function for details.
  const chartBreakdown = useSelector(selectBreakdown);
  const alignment = useSelector(selectAlignment);
  const dateComparison = useSelector(selectDateComparison);
  const visualFilters = useSelector(selectVisualFilters);
  const targetDate = useSelector(selectTargetDate);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const paceHorizon = useSelector(selectPaceHorizon);
  const paceVariable = useSelector(selectPaceVariable);
  const alignmentToggle = useSelector(selectSearchAlignmentToggle);
  const symmetricComparisonToggle = useSelector(selectSymmetricComparisonToggle);
  const isSignIn = useSelector(selectIsSignedIn);
  const customBooking = useSelector(selectSearchOtb);
  const isDetailed = useSelector(selectIsDetailed);
  const customDate = useSelector(selectSearchCustomDate);
  const startDate = useSelector(selectSearchStartDate);
  const endDate = useSelector(selectSearchEndDate);
  const visualToggle = useSelector(selectSearchVisualToggle);
  const symmetricComparisonIsDisplay = useSelector(selectSymmetricComparisonIsDisplay);
  const summaryForecastFilter = useSelector(selectSummaryForecastFilter);
  const hotelList = useSelector(selectHotelDropdownList);
  const hotelFocusOn = useSelector(selectHotelFocusOn);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const hotelGroupList = useSelector(selectHotelGroupDropdownList);
  const normalizedViewToggle = useSelector(selectNormalizedView);
  const isSummaryHotelComparison = useSelector(selectSearchHotelComparison);
  const mergedHotelList = useSelector(selectMergedHotelList);
  const isHotelForecast = useSelector(selectsHotelForecast);
  const originalHotelFocusOn = useSelector(selectOriginalHotelFocusOn);
  const isDemoModeActivated = useSelector(selectIsDemoModeActivated);
  const isHotelSetOverTimeDisabled = useSelector(selectIsSecondaryHotelSetOverTimeDisabled);
  const hotelSetOverTime = useSelector(selectIsSecondaryHotelSetOverTime);
  const selectedHotelName = useSelector(selectHotelName);
  const invalidPrimaryHotels = useSelector(selectInvalidSecondaryHotelList);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const hasLeap = useSelector(selectIsSearchLeapComparison);
  const percentage = useSelector(selectPercentage);
  const isMovingAverage = useSelector(selectIsMovingAverage);
  const movingAverage = useSelector(selectMovingAverage);
  const weeklyViewToggle = useSelector(selectTrendTimeWeeklyView);
  const isCumulative = useSelector(selectIsCumulative);
  const breakdownData = useSelector(selectBreakdownDetails);
  const focusOn = useSelector(selectFocusOn);
  const selectedScenario = useSelector(selectSelectedScenario);
  const scenarios = useSelector(selectSearchData);
  //
  const [dateComparisonList, setDateComparisonList] = useState([]);
  const [chartBreakdownList, setChartBreakdownList] = useState([]);
  const [paceList, setPaceList] = useState([]);
  const [paceVariableList, setPaceVariableList] = useState([]);
  const [percentageList, setPercentage] = useState([]);
  const [movingAverageList, setMovingAverageList] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [previousFocusOnSelection, setPreviousFocusOnSelection] = useState([]);
  const [focusOnList, setFocusOnList] = useState([]);
  const [selectedBreakdown, setSelectedBreakdown] = useState();
  //
  useSearchFilter();
  // To select otb or custom booking window
  const handleOTBChange = (event) => {
    const { value } = event.target;
    dispatch(searchActions.setSearchOtb(value !== 'false'));
    dispatch(searchActions.setSearchBookingDate(resetBookingDate));
  };
  // Reset button function to set filter panel values to default
  const clearSearch = () => {
    dispatch(searchActions.setNormalizedView(false));
    dispatch(searchActions.setSearchOtb(false));
    dispatch(dashboardActions.setSummaryForecastFilter(true));
    dispatch(
      searchActions.setBreakdown(
        visualFilters?.filter(
          (filter) => filter?.tags?.includes(TAGS.SEGMENT_BREAKDOWN) && filter?.isDefault === true
        )[0]?.id
      )
    );
    dispatch(
      dashboardActions.setPaceHorizon(
        visualFilters?.filter(
          (filter) => filter?.tags?.includes(TAGS.PACE_HORIZON) && filter?.isDefault === true
        )[0]?.id
      )
    );
    dispatch(
      dashboardActions.setPaceVariable(
        visualFilters?.filter(
          (filter) =>
            filter?.tags?.includes(TAGS.SUMMARY_PACE_VARIABLE) && filter?.isDefault === true
        )[0]?.values[0]
      )
    );
    dispatch(searchActions.setSymmetricComparisonToggle(true));
    const subAlignmentFilter = getSubAlignmentFilter(visualFilters, false);
    dispatch(
      searchActions.setDateComparison(
        subAlignmentFilter?.values?.filter((filter) => filter.isDefault === true)[0].value
      )
    );
    dispatch(searchActions.setSearchBookingDate(resetBookingDate));
    dispatch(dashboardActions.setPmsSync(false));
    dispatch(searchActions.setSearchAlignmentToggle(subAlignmentFilter?.type === ALIGNED));
    dispatch(searchActions.setIsDetailed(false));
    dispatch(searchActions.setSearchStartDate(STAY_DATE.startDate));
    dispatch(searchActions.setSearchEndDate(STAY_DATE.endDate));
    dispatch(searchActions.setSearchCustomDate(KEYS.START_DATE));
    dispatch(searchActions.setSearchDateSelected(STAY_DATE.startDate));
    //
    setPreviousFocusOnSelection(
      chartBreakdownList.reduce((acc, breakdown) => {
        acc[breakdown.id] = { detailedOn: [], detailedOff: [] };
        return acc;
      }, {})
    );
  };

  // Triggered when visualFilters value changes
  useEffect(() => {
    // Set pace filter list and breakdown list
    const subBreakdownList = getSubBreakdownFilter(visualFilters, TAGS.BREAKDOWN_SUMMARY);
    if (subBreakdownList) {
      setChartBreakdownList(subBreakdownList);
      setPreviousFocusOnSelection(
        subBreakdownList.reduce((acc, breakdown) => {
          acc[breakdown.id] = { detailedOn: [], detailedOff: [] };
          return acc;
        }, {})
      );
    }
    const subPaceFilter = getSubPaceFilter(visualFilters);
    if (subPaceFilter) {
      setPaceList(subPaceFilter);
    }
    const subPaceVariableFilter = getSubPaceVariableFilter(visualFilters);
    if (subPaceVariableFilter) {
      setPaceVariableList(subPaceVariableFilter);
    }
    // Set percentage list
    const percentageItemList = getPercentageList(visualFilters);
    if (percentageList) {
      setPercentage(percentageItemList);
    }
    const movingAvgList = getMovingAverageFilter(visualFilters);
    if (movingAvgList) {
      setMovingAverageList(movingAvgList);
    }
  }, [visualFilters]);

  // Triggered when alignment, visualFilters, alignmentToggle changes
  useEffect(() => {
    // Set date comparison list based on alignment
    if (alignment) {
      const dateComparisonFilter = getDateComparisonFilter(visualFilters, alignment?.id);
      setDateComparisonList(dateComparisonFilter);
      // persist value on browser refresh
      const serializedState = JSON.parse(
        localStorage.getItem(`persist:${ENVIRONMENT.PERSIST_KEY}`)
      );
      //
      if (!isSignIn && serializedState) {
        return;
      }
      dispatch(
        searchActions.setDateComparison(
          dateComparisonFilter?.filter((filter) => filter?.isDefault === true)[0].value
        )
      );
    }
  }, [alignment, visualFilters, alignmentToggle]);

  // Triggered on change of chartBreakdown, visualFilters, breakdownData or selectedBreakdown
  useEffect(() => {
    // Set focus on list relevant for selected chart breakdown
    if (selectedBreakdown) {
      const focusOnFilter = getFocusOnFilter(breakdownData, selectedBreakdown);
      setFocusOnList(focusOnFilter);
      if (isEmpty(previousFocusOnSelection)) {
        dispatch(
          searchActions.setFocusOn(
            focusOnFilter?.filter(
              (option) => option?.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
            )
          )
        );
      }
      if (
        isDetailed &&
        !isEmpty(previousFocusOnSelection) &&
        isEmpty(previousFocusOnSelection[chartBreakdown].detailedOn)
      ) {
        dispatch(
          searchActions.setFocusOn(
            focusOnFilter?.filter(
              (option) => option?.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
            )
          )
        );
      } else if (
        !isDetailed &&
        !isEmpty(previousFocusOnSelection) &&
        isEmpty(previousFocusOnSelection[chartBreakdown].detailedOff)
      ) {
        dispatch(
          searchActions.setFocusOn(
            focusOnFilter?.filter(
              (option) => option?.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
            )
          )
        );
      }
    }
  }, [visualFilters, breakdownData, selectedBreakdown]);

  // Triggered on visualFilters, chartBreakdown or isDetails toggle change
  useEffect(() => {
    // Assign values to selected breakdown
    const breakdown = getSelectedBreakdown(visualFilters, chartBreakdown, isDetailed);
    setSelectedBreakdown(breakdown);
  }, [isDetailed, chartBreakdown, visualFilters]);

  // Triggered when alignmentToggle or visualFilters value changes
  useEffect(() => {
    const subAlignmentFilter = getSubAlignmentFilter(visualFilters, alignmentToggle);
    dispatch(
      searchActions.setAlignment({
        id: subAlignmentFilter?.id,
        label: capitalizeFirst(subAlignmentFilter?.type),
      })
    );
  }, [alignmentToggle, visualFilters]);

  // Alignment toggle change handler
  const handleToggleChange = (event) => {
    dispatch(searchActions.setSearchAlignmentToggle(event.target.checked));
    const dateComparisonFilter = getDateComparisonFilter(visualFilters, alignment?.id);
    dispatch(
      searchActions.setDateComparison(
        dateComparisonFilter?.filter((filter) => filter?.isDefault === true)[0].value
      )
    );
  };
  // Date comparison change handler
  const handleComparisonChange = (event) => {
    dispatch(searchActions.setDateComparison(event.target.value));
    dispatch(searchActions.setSearchDateSelected(STAY_DATE.startDate));
    dispatch(searchActions.setSearchCustomDate(KEYS.START_DATE));
    //
    const dateRange = getComparisonDateRange(
      STAY_DATE.startDate,
      targetDate.startDate,
      targetDate.endDate,
      KEYS.START_DATE
    );
    const newValue = COMPARISON_TO_FILTER_MAP[event.target.value];
    //
    const { updatedScenarios } = updateScenarioFilter({
      scenario: selectedScenario,
      filterKey: SCENARIO_FILTERS.COMPARISON_PERIOD,
      value: newValue,
      keyword: newValue,
      allScenarios: scenarios,
    });
    //
    dispatch(searchActions.setSearchScenario(updatedScenarios));
    //
    dispatch(searchActions.setSearchStartDate(dateRange?.comparisonStartDate.toISOString()));
    dispatch(searchActions.setSearchEndDate(dateRange?.comparisonEndDate.toISOString()));
  };

  // Triggered when customBooking, dateComparison, visualFilters, isLoaded change
  useEffect(() => {
    // Assign default value to date comparison when OTB is selected for booking window
    if (!customBooking && dateComparison === OTB_VALUE && isLoaded) {
      const subAlignmentFilter = getSubAlignmentFilter(visualFilters, true);
      dispatch(
        searchActions.setDateComparison(
          subAlignmentFilter?.values?.filter((filter) => filter.isDefault === true)[0].value
        )
      );
    }
  }, [customBooking, dateComparison, visualFilters, isLoaded]);

  /**
   * set search pace horizon value
   * @param {*} event - on change event
   */
  const handlePaceHorizonChange = (event) => {
    const foundPace = getSubPaceFilter(visualFilters).find(
      (pace) => pace.id === event.target.value
    );
    //
    const { updatedScenarios } = updateScenarioFilter({
      scenario: selectedScenario,
      filterKey: SCENARIO_FILTERS.BOOKING_PACE_HORIZON,
      value: foundPace.label?.[0],
      keyword: foundPace.label?.[0],
      allScenarios: scenarios,
    });
    //
    dispatch(searchActions.setSearchScenario(updatedScenarios));
    //
    dispatch(dashboardActions.setPaceHorizon(event.target.value));
  };
  /**
   * set search pace horizon value
   * @param {*} event - on change event
   */
  const handleForecastFilterChange = (event) => {
    dispatch(dashboardActions.setSummaryForecastFilter(event.target.checked));
  };
  /**
   * set search pace variable value
   * @param {*} event - on change event
   */
  const handlePaceVariableChange = (event) => {
    dispatch(dashboardActions.setPaceVariable(event.target.value));
  };
  /**
   * set symmetric comparison value
   * @param {*} event - on change event
   */
  const handleSymmetricComparisonChange = (event) => {
    dispatch(searchActions.setSymmetricComparisonToggle(event.target.checked));
  };
  /**
   * set custom dates value
   * @param {*} event - on change event
   */
  const handleCustomDates = (event) => {
    dispatch(searchActions.setSearchCustomDate(event.target.value));
  };
  /**
   * set breakdown value
   * @param {*} event - onChanged event
   */
  const handleBreakdownChange = (event) => {
    dispatch(searchActions.setBreakdown(event.target.value));
    //
    const found = getSubBreakdownFilter(visualFilters, TAGS.BREAKDOWN_SUMMARY).find(
      (breakdown) => breakdown.id === event.target.value
    );
    //
    const { updatedScenarios } = updateScenarioFilter({
      scenario: selectedScenario,
      filterKey: SCENARIO_FILTERS.BREAKDOWN,
      value: found?.label,
      keyword: found?.label,
      allScenarios: scenarios,
    });
    //
    dispatch(searchActions.setSearchScenario(updatedScenarios));
  };
  /**
   * set isDetailed value
   * @param {*} event - onChanged event
   */
  const handleIsDetailedChange = (event) => {
    dispatch(searchActions.setIsDetailed(event.target.checked));
  };
  /**
   * set percentage value
   * @param {*} event - on changed event
   */
  const handlePercentageChange = (event) => {
    dispatch(searchActions.setPercentage(event.target.value));
    //
    const found = getPercentageList(visualFilters).find(
      (percentageValue) => percentageValue.id === event.target.value
    );
    //
    const { updatedScenarios } = updateScenarioFilter({
      scenario: selectedScenario,
      filterKey: SCENARIO_FILTERS.PERCENTAGE,
      value: found?.label,
      keyword: found?.label,
      allScenarios: scenarios,
    });
    //
    dispatch(searchActions.setSearchScenario(updatedScenarios));
  };
  //
  const focusOnLabel = focusOn?.length ? 'Focused on' : 'Select Focus on';
  //
  return (
    <>
      {availableFilters.includes(FILTERS.PMS) && (
        <>
          <PmsToggle
            disabled={toggleDisabled}
            value={pmsSyncToggle}
            onChange={(event) => dispatch(dashboardActions.setPmsSync(event.target.checked))}
          />
          <br />
        </>
      )}
      {availableFilters.includes(FILTERS.BOOKING_WINDOW) && (
        <>
          <BookingWindow
            vOTB={customBooking}
            customBooking={customBooking}
            handleOTBChange={handleOTBChange}
            reportType={reportType}
            latestDate={latestDate}
            otbValue={false}
          />
          <br />
        </>
      )}
      {availableFilters.includes(FILTERS.BREAKDOWN) && (
        <>
          <BreakdownWindow
            isDetailed={isDetailed}
            chartBreakdown={chartBreakdown}
            chartBreakdownList={chartBreakdownList?.sort(
              (first, second) => labelOrder.indexOf(first.label) - labelOrder.indexOf(second.label)
            )}
            handleIsDetailedChange={handleIsDetailedChange}
            handleBreakdownChange={handleBreakdownChange}
            focusOnList={focusOnList}
            focusOn={focusOn}
            focusOnLabel={focusOnLabel}
            isFocusOnAvailable
            handleFocusOnChange={(event, value, reason) => {
              if (reason === 'selectOption' || reason === 'removeOption') {
                if (
                  value.find(
                    (option) => option.label === COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
                  ) &&
                  value?.filter(
                    (option) => option.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
                  ).length === focusOn.length &&
                  value?.length === focusOnList.length
                ) {
                  const allOptions = [];
                  dispatch(searchActions.setFocusOn(allOptions));
                  if (previousFocusOnSelection[chartBreakdown]) {
                    if (isDetailed) {
                      previousFocusOnSelection[chartBreakdown].detailedOn = allOptions;
                    } else {
                      previousFocusOnSelection[chartBreakdown].detailedOff = allOptions;
                    }
                  }

                  setAllSelected(false);
                } else if (
                  value.find((option) => option.label === COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label)
                ) {
                  // Select all options if "Select All" is not selected
                  const allOptions = focusOnList.filter(
                    (option) => option.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
                  );
                  dispatch(searchActions.setFocusOn(allOptions));
                  if (previousFocusOnSelection[chartBreakdown]) {
                    if (isDetailed) {
                      previousFocusOnSelection[chartBreakdown].detailedOn = allOptions;
                    } else {
                      previousFocusOnSelection[chartBreakdown].detailedOff = allOptions;
                    }
                  }
                  setAllSelected(true);
                } else {
                  dispatch(searchActions.setFocusOn(value));
                  if (previousFocusOnSelection[chartBreakdown]) {
                    if (isDetailed) {
                      previousFocusOnSelection[chartBreakdown].detailedOn = value;
                    } else {
                      previousFocusOnSelection[chartBreakdown].detailedOff = value;
                    }
                  }
                  setAllSelected(
                    value?.filter(
                      (option) => option.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
                    ).length ===
                      focusOnList?.filter(
                        (option) => option.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
                      ).length
                  );
                }
              }
            }}
            allSelected={allSelected}
          />
          <br />
        </>
      )}
      {availableFilters.includes(FILTERS.MOVING_AVERAGE) && (
        <>
          <MovingAverageWindow
            movingAverage={movingAverage}
            movingAverageList={movingAverageList ?? []}
            handleMovingAverageChange={(event, value) =>
              dispatch(searchActions.setMovingAverage(value))
            }
            handleIsMovingAverageChange={(event) => {
              dispatch(searchActions.setIsMovingAverage(event.target.checked));
              if (event.target.checked) {
                dispatch(searchActions.setIsTrendDeAggregate(false));
              }
            }}
            isMovingAverage={isMovingAverage}
            isDisabled={isCumulative || weeklyViewToggle}
            id="search-trend-moving-average"
          />
          <br />
        </>
      )}
      {availableFilters.includes(FILTERS.VS_HOTEL) && (
        <>
          <CompareHotelsWindow
            selectedHotelName={selectedHotelName}
            hotelList={hotelList ?? []}
            mergedHotelList={mergedHotelList ?? []}
            focusOn={hotelFocusOn ?? []}
            focusOnLabel={hotelFocusOn?.length ? 'Compared with' : 'Select Hotel'}
            handleCompareHotelsChange={(event, value) => {
              dispatch(searchActions.setHotelFocusOn(value));
              dispatch(dashboardActions.setIsSecondaryHotelSetOverTime(false));
            }}
            normalizedViewToggle={normalizedViewToggle}
            handleTrendHotelNormalize={(event) =>
              dispatch(searchActions.setNormalizedView(event.target.checked))
            }
            enableSecondaryHotelGroups={enableSecondaryHotelGroups}
            hotelGroupList={hotelGroupList}
            originalHotelFocusOn={originalHotelFocusOn}
            isDemoModeActivated={isDemoModeActivated}
            setOriginalHotelFocusOn={(focus) => {
              dispatch(searchActions.setOriginalHotelFocusOn(focus));
            }}
            resetFocusOn={(focus) => {
              dispatch(searchActions.resetHotelFocusOn(focus));
            }}
            hotelSetOverTime={hotelSetOverTime}
            handleHotelSetChange={(event) => {
              dispatch(dashboardActions.setIsSecondaryHotelSetOverTime(event.target.checked));
              if (hotelFocusOn?.id === selectedHotelName?.id) {
                dispatch(dashboardActions.setIsPrimaryHotelSetOverTime(event.target.checked));
              }
            }}
            isHotelSetOverTimeDisabled={isHotelSetOverTimeDisabled}
            invalidPrimaryHotels={invalidPrimaryHotels}
          />
          <br />
        </>
      )}
      {availableFilters.includes(FILTERS.TIME_COMPARISON) && (
        <>
          <VsComparisonWindow
            customBooking={customBooking}
            alignmentToggle={alignmentToggle ?? false}
            handleToggleChange={handleToggleChange}
            symmetricComparisonToggle={symmetricComparisonToggle}
            dateComparison={dateComparison}
            dateComparisonList={dateComparisonList}
            handleComparisonChange={handleComparisonChange}
            handleSymmetricComparisonChange={handleSymmetricComparisonChange}
            isCustomOptionAvailable
            customDate={customDate}
            reportType={reportType}
            startDate={startDate}
            endDate={endDate}
            targetDate={targetDate}
            handleCustomDates={handleCustomDates}
            isDisplay={symmetricComparisonIsDisplay}
            isDisplayOnClickAction={() => {
              dispatch(
                searchActions.setSymmetricComparisonIsDisplay(!symmetricComparisonIsDisplay)
              );
            }}
            isTimeComparisonDisabled={isSummaryHotelComparison}
            hasLeap={hasLeap}
          />
          <br />
        </>
      )}
      {availableFilters.includes(FILTERS.BOOKING_HORIZON) && (
        <>
          <BookingPaceWindow
            paceHorizon={paceHorizon}
            paceList={paceList}
            paceVariable={paceVariable}
            handlePaceHorizonChange={handlePaceHorizonChange}
            handlePaceVariableChange={handlePaceVariableChange}
            isPlot={false}
            paceVariableList={paceVariableList}
            visualToggle={visualToggle}
            handleVisualToggleChange={(event) =>
              dispatch(searchActions.setSearchVisualToggle(event.target.value))
            }
            handleForecastFilterChange={handleForecastFilterChange}
            summaryForecastFilter={summaryForecastFilter != null ? summaryForecastFilter : false}
            enableForecastFilter={organization?.enableForecastFilter && isHotelForecast}
            showFilter
          />
          <br />
        </>
      )}
      {availableFilters.includes(FILTERS.PERCENTAGE) && (
        <>
          <PercentageWindow
            percentage={percentage}
            percentageList={percentageList}
            handlePercentageChange={handlePercentageChange}
          />
          <br />
        </>
      )}

      <CustomStack cssClassName={styles.innerFilter}>
        <ActionButton onClick={clearSearch} label="RESET" />
      </CustomStack>
      <Toolbar />
      <DataUpdatedLabel
        latestDate={latestDate}
        hotelTimezone={hotelTimezone}
        toggleDisabled={toggleDisabled}
      />
    </>
  );
};
//
export default SearchFilter;
